import {updateTrackingConfiguration} from '@js/actions/trackingActions';

import PWAManager from '@js/modules/pwaManager';
import {hasBotCharacteristics} from '@js/modules/botDetection';


class TrackingManager {
    _initialTrackingDelay = 750; // in ms, settle delay before starting the dwell timer
    _dwellDelay = 2000;          // in ms, foreground time before a no-interaction visit is confirmed

    // Genuine human interactions that confirm a visit.
    _engagementEvents = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'wheel', 'touchstart'];
    _confirmed = false;

    /* Public methods
    ******************** */
    // A visit is confirmed as human only on a genuine signal — a trusted user
    // interaction, or enough foreground dwell time — never merely because the
    // page loaded. Headless bots that just load and leave stay unconfirmed
    // (validated:false server-side). Cookie refusal / adblock are NOT treated as
    // bot signals: a real user who rejects cookies still counts.
    initialize = (forceRequest) => {
        // A consent accept/deny is itself a genuine human interaction.
        if (forceRequest) {
            this._confirmVisit();
            return;
        }

        if (this._alreadySent()) return;

        // Confirm on the first genuine (trusted) interaction...
        this._bindEngagement();

        // ...or after enough foreground dwell time, unless the client shows
        // strong bot characteristics — then only a trusted interaction confirms.
        setTimeout(() => {
            if (this._confirmed || hasBotCharacteristics()) return;

            const focused = typeof document.hasFocus !== 'function' || document.hasFocus();

            if (document.visibilityState === 'visible' && focused) {
                this._confirmVisit();
            }
        }, this._initialTrackingDelay + this._dwellDelay);
    };

    /* Private methods
    ******************** */
    _onEngagement = (event) => {
        // Ignore synthetic events dispatched by bots.
        if (event && event.isTrusted === false) return;

        this._confirmVisit();
    };

    _bindEngagement = () => {
        // No { once: true }: an untrusted (bot) event would otherwise consume the
        // listener before a later trusted one. Removed on confirmation instead.
        this._engagementEvents.forEach((name) => window.addEventListener(name, this._onEngagement, {capture: true, passive: true}));
    };

    _unbindEngagement = () => {
        this._engagementEvents.forEach((name) => window.removeEventListener(name, this._onEngagement, true));
    };

    // Send the visit confirmation to the server (validated = true).
    _confirmVisit = () => {
        if (this._confirmed) return;

        this._confirmed = true;
        this._unbindEngagement();

        if (this._alreadySent()) return;

        const properties = {
            valid: true
        };

        if (PWAManager.getPWADisplayMode() === PWAManager.MODE.PWA) {
            properties.pwa = true;
        }

        // The visit being confirmed, read now: by the time the beacon answers, the cookie may be the next visit's.
        const visit = this._visitToken();

        updateTrackingConfiguration(properties)
            .then(() => {
                if (!this._rejectCookies()) {
                    this._markVisit(visit);
                }
            })
            .catch(() => {
                if (!this._rejectCookies()) {
                    document.cookie = '';
                }
            });

        if (!this._rejectCookies()) {
            this._markVisit(visit);
        }
    };

    // The marker holds Ahoy's visit token: a bare session cookie outlived the 12 h visit, and the
    // visitor's next visit was never confirmed.
    _visitToken = () => {
        const match = document.cookie.match(/(?:^|; )ahoy_visit=([^;]*)/);
        return match ? match[1] : null;
    };

    _markVisit = (visit) => {
        if (visit) document.cookie = `configurationSet=${visit};path=/`;
    };

    _rejectCookies = () => {
        if (navigator.cookieEnabled) return false;
        if (!document.cookie.indexOf) return false;

        // set and read cookie
        document.cookie = 'cookietest=1';
        const ret = document.cookie.indexOf('cookietest=') === -1;

        // delete cookie
        document.cookie = 'cookietest=1; expires=Thu, 01-Jan-1970 00:00:01 GMT';

        return ret;
    };

    _alreadySent = () => {
        if (this._rejectCookies()) return false;

        const visit = this._visitToken();
        const marker = document.cookie.match(/(?:^|; )configurationSet=([^;]*)/);
        return visit !== null && marker !== null && marker[1] === visit;
    };
}

export default new TrackingManager();
